import type { ReactNode } from 'react';
import type { FormHelperTextProps, SxProps } from '@mui/material';

import { Stack } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { TextField, theme } from '@theguarantors/tg-package-material-ui';

import { createDataTestId } from '@/utils/createDataTestId';
import { FieldHelperText, fieldHelperTextSx } from '../../components/field-helper.text';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';

// Shared disabled/read-only look for engine inputs (white bg, grey border + text), so the disabled
// address picker matches the disabled text inputs (used via `inputSx` in the address field).
export const disabledLeaseInputSx: SxProps = {
  '.MuiInputLabel-root': {
    color: theme.palette.neutralGrey[300],
    width: '100%',
  },
  '& .MuiFilledInput-input.Mui-disabled': {
    backgroundColor: 'white',
    border: `1px solid ${theme.palette.neutralGrey[200]}`,
    color: theme.palette.neutralGrey[300],
    borderRadius: '4px',
  },
};

export type LeaseTextInputProps = {
  name: string;
  title: string;
  disabled?: boolean;
  helperText?: ReactNode;
  placeholder?: string;
  required?: boolean;
  type?: 'text' | 'number' | 'date';
  /** Rendered inside the field (e.g. an inline remove button for array rows). */
  endAdornment?: ReactNode;
  onBlurValue?: (value: string | undefined) => void;
  onChangeValue?: (value: string) => void;
  errorHelperText?: ReactNode;
};

export const LeaseTextInput = ({
  disabled = false,
  helperText,
  name,
  placeholder,
  required = true,
  title,
  type = 'text',
  endAdornment,
  onBlurValue,
  onChangeValue,
  errorHelperText,
}: LeaseTextInputProps) => {
  const { watch, setValue, trigger, getFieldState, formState } = useFormContext();
  const { inputSize: size, asteriskColor } = useLeaseDetailsUI();
  const resolvedAsteriskColor = asteriskColor ?? theme.palette.red[600];

  const value = watch(name) as string | undefined;
  // `getFieldState` walks dotted paths (e.g. `applicants.0.email`) where a
  // bare `formState.errors[name]` lookup would return undefined.
  const error = getFieldState(name, formState).error;
  const hasCustomErrorHelperText = errorHelperText != null;
  const isError = !!error?.message;
  const isEmptyValue = value == null || value === '';
  const showCustomErrorHelper = isError && hasCustomErrorHelperText && isEmptyValue;
  // Suppress the static helper while an inline error is showing — error and
  // supporting copy never render at the same time.
  const supportingText = showCustomErrorHelper ? errorHelperText : isError ? undefined : helperText;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: nextValue } = event?.target ?? {};
    if (nextValue == null) return;

    setValue(name, nextValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
    trigger(name);
    onChangeValue?.(nextValue);
  };

  const onBlur = () => {
    trigger(name);
    onBlurValue?.(value);
  };

  const sx = disabled ? disabledLeaseInputSx : {};

  const dataTestId = createDataTestId({ inputString: name, suffix: 'input' });

  return (
    <Stack direction="column" sx={{ width: '100%' }}>
      <TextField
        color="secondary"
        id={dataTestId}
        data-testid={dataTestId}
        disabled={disabled}
        error={isError}
        helperText={isError && !showCustomErrorHelper ? error?.message?.toString() : undefined}
        label={title}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        size={size}
        slotProps={endAdornment ? { input: { endAdornment } } : undefined}
        FormHelperTextProps={
          {
            'data-testid': `${dataTestId}-error-text`,
            sx: fieldHelperTextSx,
          } as FormHelperTextProps & { 'data-testid': string }
        }
        sx={{
          '& .MuiInputBase-root': {
            width: '100%',
          },
          '& .MuiFormLabel-asterisk': {
            color: `${resolvedAsteriskColor} !important`,
          },
          '& .MuiInputLabel-root.Mui-disabled .MuiFormLabel-asterisk': {
            color: 'inherit !important',
          },
          '& .MuiInputLabel-root': {
            [theme.breakpoints.down('md')]: { fontSize: '14px !important' },
          },
          width: '100%',
          ...sx,
        }}
        type={type}
        value={value || ''}
      />
      {supportingText && (
        <FieldHelperText
          text={supportingText}
          disabled={disabled}
          error={showCustomErrorHelper}
          dataTestId={`${dataTestId}-helper-text`}
        />
      )}
    </Stack>
  );
};
